/**
 * Global, imperative open/close state for entity create/edit panels -
 * generalizes _layouts/navigation/hooks/useQuickCreateModals's plain-state
 * approach (rather than resurrecting the removed ModalContext + lazy-
 * workflow-registry pattern, see docs/futurescope/ai-tool-calls-spec.md in
 * simplifyserver for why that's a meaningful difference) so any component -
 * not just Navigation's Quick Actions menu - can open a panel by its
 * tool-call name and await the record it produces.
 *
 * Plain module state + pub-sub, not a hook, so `openModal` is callable from
 * deep inside a component (e.g. CompanySelect's "+ Create new company" row)
 * without that component needing to own or forward any modal state itself.
 *
 * A real stack (not a single slot), not just one deep - so a picker's own
 * "+ Add new X" footer opened FROM one of these panels (e.g. Usage
 * Compilation's "Select billing unit"/"Select usage source" fields, once
 * those got the same footerAction as Market/Company) can push a second
 * panel on top instead of discarding whatever's already open underneath
 * it. Previously `openModal()` unconditionally settled any pending entry
 * with `null` before replacing it - fine as long as only one caller ever
 * had a panel open at a time, but a nested caller (a field inside an
 * already-open entity modal) silently closed and discarded that outer
 * modal the instant its own inline-create footer was clicked. `stack[]`
 * is a fresh array on every mutation (not pushed/spliced in place) so
 * `useSyncExternalStore`'s reference-equality check in ModalHost sees the
 * change; `resolveModal`/`closeModal` now take the specific entry's `id`
 * (ModalHost binds each rendered panel's onClose/onSuccess to its own
 * entry) so settling one panel never touches any other still-open one.
 */
let stack = [];
let nextId = 0;
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());

export const getModalState = () => stack;

export const subscribeToModalState = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const settle = (id, result) => {
  const entry = stack.find((e) => e.id === id);
  if (!entry) return;
  stack = stack.filter((e) => e.id !== id);
  emit();
  entry.resolve(result ?? null);
};

/**
 * Opens the panel registered for `toolName` (see registry.js) and returns a
 * promise that resolves with the created/updated record, or null if the
 * user closes the panel without submitting. Stacks on top of any panel(s)
 * already open rather than replacing them - see this module's own doc
 * comment above.
 */
export const openModal = (toolName, params = {}) => {
  const id = ++nextId;
  return new Promise((resolve) => {
    stack = [...stack, { id, toolName, params, resolve }];
    emit();
  });
};

export const resolveModal = (id, result) => settle(id, result ?? null);
export const closeModal = (id) => settle(id, null);
